import { ResponsiveModal } from 'components/ResponsiveModal'
import {
  useOrderingTypeContext,
  useVenueContext,
} from 'contexts/VenueOrderContext'
import { useCurrentSheet } from 'hooks/useCurrentSheet'
import { useLogger } from 'hooks/useLogger'
import { CartItemPartsFragment, useAddItemsToCartMutation } from 'lib/gql'
import React, { useMemo, useState } from 'react'

import { AnotherRoundSheet } from './AnotherRoundSheet'

interface Props {
  isCartEmpty?: boolean
  anotherRoundCartItems?: CartItemPartsFragment[] | null
}

export type Quantities = Record<string, number>

export const AnotherRoundModal = ({
  isCartEmpty,
  anotherRoundCartItems,
}: Props) => {
  const [isClosed, setIsClosed] = useState(false)
  const [quantities, setQuantities] = useState<Quantities>({})
  const { orderingType } = useOrderingTypeContext()
  const { venueSlug } = useVenueContext()
  const currentSheet = useCurrentSheet()
  const { sendLog } = useLogger()

  const [{ fetching }, addItemsToCart] = useAddItemsToCartMutation()

  const filteredCartItems = useMemo(
    () =>
      anotherRoundCartItems?.filter(
        ({ id }) => quantities[id] === undefined || quantities[id] !== 0,
      ) ?? [],
    [anotherRoundCartItems, quantities],
  )

  const cartItemCount = useMemo(
    () =>
      anotherRoundCartItems?.reduce((total, lineItem) => {
        const upsellCount = (lineItem?.cartUpsells || []).reduce(
          (upsellTotal, upsell) => {
            return upsellTotal + (quantities[lineItem.id] ?? upsell.quantity)
          },
          0,
        )
        return (
          total + upsellCount + (quantities[lineItem.id] ?? lineItem.quantity)
        )
      }, 0) ?? 0,
    [anotherRoundCartItems, quantities],
  )

  const handleOnSubmit = async () => {
    const mappedItems = filteredCartItems.map(
      ({ id, item, quantity, cartModifiers, cartUpsells }) => ({
        item: { id: item!.id, quantity: quantities[id] ?? quantity },
        modifiers: cartModifiers?.map((modifier) => ({
          id: modifier.modifier!.id,
          quantity: quantities[id] ?? modifier.quantity,
        })),
        upsells: cartUpsells?.map((upsell) => ({
          id: upsell.upsell!.id,
          upsellGroupId: upsell.upsellGroupId!,
          quantity: quantities[id] ?? upsell.quantity,
        })),
      }),
    )
    await addItemsToCart({
      input: {
        venueSlug,
        orderingType,
        items: mappedItems,
      },
    })
    sendLog('Another Round items added', {
      items: mappedItems,
    })
  }

  const handleOnClose = () => {
    setIsClosed(true)
  }

  return (
    <ResponsiveModal onClose={handleOnClose}>
      {!currentSheet &&
        isCartEmpty &&
        filteredCartItems.length &&
        !isClosed && (
          <AnotherRoundSheet
            isSubmitting={fetching}
            quantities={quantities}
            cartItems={filteredCartItems}
            cartItemCount={cartItemCount}
            onChange={setQuantities}
            onSubmit={handleOnSubmit}
            onClose={handleOnClose}
          />
        )}
    </ResponsiveModal>
  )
}
